import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { msgErro } from '../services/ui';
import { useSort } from '../helpers/useSort';
import SeletorFornecedor from '../components/SeletorFornecedor';
import './MeuMix.css';

const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const fmtEstoque = (v, unid) => {
  const n = Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  const u = String(unid || '').toUpperCase() === 'M2' ? 'm²' : String(unid || 'un').toLowerCase();
  return `${n} ${u}`;
};
const ehVendavel = (d) => { const m = norm(d); return m.includes('de linha') || m.includes('encomenda'); };
const brl = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// '2026-08-22' -> '22/08/26' (sem new Date, que desloca por fuso).
const fmtDataCurta = (iso) => {
  const [a, m, d] = String(iso || '').split('-');
  return a ? `${d}/${m}/${a.slice(2)}` : '—';
};
const diasDesde = (iso) => {
  if (!iso) return null;
  const [a, m, d] = iso.split('-').map(Number);
  return Math.floor((Date.now() - Date.UTC(a, m - 1, d)) / 86400000);
};

// Dropdown de filtro com checkboxes (multi-seleção). Fecha em click-fora/ESC. Padrão do catálogo do arquiteto.
function FiltroMulti({ label, opcoes, selecionados, onToggle, onLimpar, buscavel, contagem }) {
  const [aberto, setAberto] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    const onKey = (e) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [aberto]);
  useEffect(() => { if (!aberto) setQ(''); }, [aberto]);
  useEffect(() => { if (aberto && buscavel) inputRef.current?.focus(); }, [aberto, buscavel]);

  const n = selecionados.length;
  const termo = norm(q);
  const filtradas = (buscavel && termo) ? opcoes.filter((op) => norm(op).includes(termo)) : opcoes;
  const marcar = (op) => { onToggle(op); if (buscavel) inputRef.current?.focus(); };

  return (
    <div className="cat-multi" ref={ref}>
      <button type="button" className={`cat-multi-btn ${n ? 'tem' : ''} ${aberto ? 'aberto' : ''}`} onClick={() => setAberto((a) => !a)}>
        <span>{label}{n ? ` · ${n}` : ''}</span><i className="bi bi-chevron-down chev" />
      </button>
      {aberto && (
        <div className="cat-multi-pop">
          <div className="cat-multi-topo">
            <span>{label}{n ? ` · ${n} marcado${n > 1 ? 's' : ''}` : ''}</span>
            {n > 0 && <button type="button" className="cat-multi-limpar" onClick={onLimpar}>Limpar</button>}
          </div>
          {buscavel && (
            <div className="cat-multi-busca">
              <i className="bi bi-search" />
              <input ref={inputRef} type="text" placeholder={`Buscar ${label.toLowerCase()}...`} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
              {q && <button type="button" className="cat-multi-busca-x" onClick={() => { setQ(''); inputRef.current?.focus(); }}><i className="bi bi-x-lg" /></button>}
            </div>
          )}
          <div className="cat-multi-lista">
            {filtradas.length === 0 && <div className="cat-multi-vazio">Nada encontrado</div>}
            {filtradas.map((op) => (
              <label key={op} className="cat-multi-item">
                <input type="checkbox" checked={selecionados.includes(op)} onChange={() => marcar(op)} />
                <span className="cat-multi-op">{op}</span>
                {contagem && <span className="cat-multi-cont">{contagem.get(op) || 0}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Card com troca de foto no hover (capa = foto 1; 2ª aparece com fade). Placeholder gracioso no 404.
function CatalogoCard({ produto, apiUrl, onAbrir }) {
  const [capaErro, setCapaErro] = useState(false);
  const [segErro, setSegErro] = useState(false);
  const isEncomenda = norm(produto.disponibilidade).includes('encomenda');
  const temEstoque = Math.round(produto.estoqueGrupo) > 0;
  const capa = `${apiUrl}/produtos/${produto.codpro}/imagem/1`;
  const segunda = `${apiUrl}/produtos/${produto.codpro}/imagem/2`;

  return (
    <div className="cat-card" onClick={onAbrir} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onAbrir(); }}>
      <div className="cat-card-img">
        {produto.promo && <span className="cat-promo-tag"><i className="bi bi-tag-fill" /> Campanha</span>}
        {capaErro && <div className="cat-img-ph"><i className="bi bi-box-seam" /></div>}
        {!capaErro && <img className="cat-img-1" src={capa} alt={produto.nome} loading="lazy" onError={() => setCapaErro(true)} />}
        {!capaErro && !segErro && <img className="cat-img-2" src={segunda} alt="" aria-hidden="true" loading="lazy" onError={() => setSegErro(true)} />}
      </div>
      <div className="cat-card-body">
        <div className="cat-tipo">{produto.tipo}</div>
        <div className="cat-nome">{produto.nome}</div>
        <div className="cat-cod">Cód {produto.codpro}</div>

        {/* Vitrine = preço anunciado: campanha vigente > preço de tabela > sob consulta
            (encomenda). O preço PRATICADO é negociação e vive só na última venda, no detalhe. */}
        {produto.promo ? (
          <div className="cat-precos promo">
            {produto.precoVista > produto.promo.por && <div className="cat-preco-de">{brl(produto.precoVista)}</div>}
            <div className="cat-preco por"><strong>{brl(produto.promo.por)}</strong><span>em campanha</span></div>
            {produto.promo.preco12x > 0 && <div className="cat-preco-parc">ou 12x de {brl(produto.promo.preco12x / 12)}</div>}
            {!produto.promo.todasFiliais && produto.promo.filiais && (
              <div className="cat-preco-filial"><i className="bi bi-geo-alt-fill" />só em {produto.promo.filiais.length} loja{produto.promo.filiais.length > 1 ? 's' : ''}</div>
            )}
          </div>
        ) : !isEncomenda && produto.precoVista > 0 ? (
          <div className="cat-precos">
            <div className="cat-preco"><strong>{brl(produto.precoVista)}</strong><span>à vista</span></div>
          </div>
        ) : (
          <div className="cat-precos">
            <div className="cat-preco"><strong className="cat-preco-consulta">Sob consulta</strong><span>preço</span></div>
          </div>
        )}

        <div className="cat-ultvenda">
          {produto.ultimaVenda ? (
            <><i className="bi bi-clock-history" />vendeu {fmtDataCurta(produto.ultimaVenda.data)}{diasDesde(produto.ultimaVenda.data) != null && diasDesde(produto.ultimaVenda.data) > 60 && <span className="parado"> · há {diasDesde(produto.ultimaVenda.data)} dias</span>}</>
          ) : (
            <><i className="bi bi-dash-circle" />sem venda registrada</>
          )}
        </div>

        <div className="cat-badges">
          <span className={`cat-badge ${isEncomenda ? 'cat-badge-encomenda' : 'cat-badge-linha'}`}>
            <i className={`bi ${isEncomenda ? 'bi-clock-history' : 'bi-check-circle'}`} />{produto.disponibilidade}
          </span>
          {temEstoque ? (
            <span className="cat-badge cat-badge-estoque"><i className="bi bi-box-seam" />{fmtEstoque(produto.estoqueGrupo, produto.unid)}</span>
          ) : (
            <span className="cat-badge cat-badge-sem"><i className="bi bi-dash-circle" />sem estoque</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MeuMix() {
  const { apiUrl } = useAuth();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState(() => sessionStorage.getItem('mm_aba') || 'catalogo');

  const [busca, setBusca] = useState('');
  const [tiposSel, setTiposSel] = useState([]);
  const [formatoSel, setFormatoSel] = useState([]);
  const [dispSel, setDispSel] = useState([]);
  const [soComEstoque, setSoComEstoque] = useState(false);
  const [soCampanha, setSoCampanha] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [pagina, setPagina] = useState(1);
  // Amostras carregam sob demanda (só ao abrir a aba)
  const [amostras, setAmostras] = useState(null);
  const [amCarreg, setAmCarreg] = useState(false);
  const [amErro, setAmErro] = useState('');
  const [fornSel, setFornSel] = useState(''); // '' = todos os CNPJs (multi-CNPJ)

  useEffect(() => {
    setCarregando(true);
    setAmostras(null); // recarrega amostras com o novo filtro ao trocar de fornecedor
    (async () => {
      try { const { data } = await api.get('/meu-mix', { params: fornSel ? { fornecedor: fornSel } : {} }); setDados(data); }
      catch (e) { setErro(msgErro(e, 'Erro ao carregar seu mix.')); }
      finally { setCarregando(false); }
    })();
  }, [fornSel]); // eslint-disable-line

  useEffect(() => {
    if (aba !== 'amostras' || amostras || amCarreg) return;
    setAmCarreg(true);
    api.get('/meu-mix/amostras', { params: fornSel ? { fornecedor: fornSel } : {} })
      .then((r) => setAmostras(r.data))
      .catch((e) => setAmErro(msgErro(e, 'Erro ao carregar as amostras.')))
      .finally(() => setAmCarreg(false));
  }, [aba, amostras, amCarreg]); // eslint-disable-line

  const amostrasFiltradas = useMemo(() => {
    if (!amostras?.produtos) return [];
    const q = norm(busca);
    return q ? amostras.produtos.filter((p) => norm(`${p.nome} ${p.codpro}`).includes(q)) : amostras.produtos;
  }, [amostras, busca]);

  const sEst = useSort('total', 'desc');   // estoque por loja — mais estoque total no grupo primeiro
  const sAm = useSort('nLojas', 'desc');   // matriz de amostras — mais amostras primeiro

  // Persiste a aba e restaura a posição ao voltar do detalhe do produto
  useEffect(() => { sessionStorage.setItem('mm_aba', aba); }, [aba]);
  const irProduto = (codpro) => { sessionStorage.setItem('mm_scroll', String(window.scrollY)); navigate(`/meu-mix/${codpro}`); };
  const scrollRestaurado = useRef(false);
  useEffect(() => {
    if (scrollRestaurado.current) return;
    const saved = sessionStorage.getItem('mm_scroll');
    if (!saved) return;
    const pronto = aba === 'amostras' ? !!amostras : !!dados;
    if (!pronto) return;
    scrollRestaurado.current = true;
    sessionStorage.removeItem('mm_scroll');
    const y = parseInt(saved, 10) || 0;
    requestAnimationFrame(() => setTimeout(() => window.scrollTo(0, y), 60));
  }, [dados, amostras, aba]);

  const toggleEm = (setter) => (op) => setter((s) => (s.includes(op) ? s.filter((x) => x !== op) : [...s, op]));
  const filtrosAtivos = tiposSel.length + formatoSel.length + dispSel.length + (soComEstoque ? 1 : 0) + (soCampanha ? 1 : 0);
  const limparFiltros = () => { setTiposSel([]); setFormatoSel([]); setDispSel([]); setSoComEstoque(false); };

  const filtrados = useMemo(() => {
    if (!dados?.produtos) return [];
    const termo = norm(busca);
    return dados.produtos.filter((p) => {
      if (tiposSel.length && !tiposSel.includes(p.tipo)) return false;
      if (formatoSel.length && !formatoSel.includes(p.formato)) return false;
      if (dispSel.length) { if (!dispSel.includes(p.disponibilidade)) return false; }
      else if (!mostrarInativos && !ehVendavel(p.disponibilidade)) return false; // padrão: mix ativo
      if (soComEstoque && !(Math.round(p.estoqueGrupo) > 0)) return false;
      if (soCampanha && !p.promo) return false;
      if (termo && !norm(`${p.nome} ${p.codpro} ${p.tipo}`).includes(termo)) return false;
      return true;
    }).sort((a, b) => (b.estoqueGrupo - a.estoqueGrupo) || a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [dados, busca, tiposSel, formatoSel, dispSel, soComEstoque, soCampanha, mostrarInativos]);

  const opcoesFiltro = useMemo(() => {
    const prods = dados?.produtos || [];
    const contar = (campo) => { const m = new Map(); for (const p of prods) { const v = p[campo]; if (v) m.set(v, (m.get(v) || 0) + 1); } return m; };
    const c = { tipo: contar('tipo'), formato: contar('formato'), disp: contar('disponibilidade') };
    const ord = (lista, m) => [...lista].sort((a, b) => (m.get(b) || 0) - (m.get(a) || 0) || String(a).localeCompare(String(b), 'pt-BR'));
    return { cont: c, tipos: ord(dados?.tipos || [], c.tipo), formatos: ord(dados?.formatos || [], c.formato), disp: ord(dados?.disponibilidades || [], c.disp) };
  }, [dados]);

  const filiais = dados?.filiais || [];
  const POR_PAGINA = 60;
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const pagina1 = filtrados.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  useEffect(() => { setPagina(1); }, [busca, tiposSel, formatoSel, dispSel, soComEstoque, soCampanha, mostrarInativos, aba]);

  const filtroBar = (
    <>
      <div className="cat-barra">
        <div className="cat-busca-wrap">
          <i className="bi bi-search" />
          <input className="cat-busca" type="search" placeholder="Buscar produto..." value={busca} onChange={(e) => setBusca(e.target.value)} autoComplete="off" />
          {busca && <button className="cat-busca-x" onClick={() => setBusca('')} type="button"><i className="bi bi-x-lg" /></button>}
        </div>
        <button type="button" className={`cat-filtros-btn ${filtrosAbertos ? 'aberto' : ''} ${filtrosAtivos ? 'tem' : ''}`} onClick={() => setFiltrosAbertos((v) => !v)}>
          <i className="bi bi-sliders2" /><span className="cat-filtros-btn-txt">Filtros</span>
          {filtrosAtivos > 0 && <span className="cat-filtros-badge">{filtrosAtivos}</span>}
        </button>
      </div>
      {filtrosAbertos && (
        <div className="cat-filtros-painel">
          <FiltroMulti label="Categoria" opcoes={opcoesFiltro.tipos} contagem={opcoesFiltro.cont.tipo} buscavel selecionados={tiposSel} onToggle={toggleEm(setTiposSel)} onLimpar={() => setTiposSel([])} />
          {opcoesFiltro.formatos.length > 0 && (
            <FiltroMulti label="Tamanho" opcoes={opcoesFiltro.formatos} contagem={opcoesFiltro.cont.formato} buscavel selecionados={formatoSel} onToggle={toggleEm(setFormatoSel)} onLimpar={() => setFormatoSel([])} />
          )}
          <FiltroMulti label="Disponibilidade" opcoes={opcoesFiltro.disp} contagem={opcoesFiltro.cont.disp} selecionados={dispSel} onToggle={toggleEm(setDispSel)} onLimpar={() => setDispSel([])} />
          <button type="button" role="switch" className={`cat-switch ${soComEstoque ? 'ativo' : ''}`} onClick={() => setSoComEstoque((v) => !v)} aria-checked={soComEstoque}>
            <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>Só com estoque
          </button>
          <button type="button" role="switch" className={`cat-switch ${soCampanha ? 'ativo' : ''}`} onClick={() => setSoCampanha((v) => !v)} aria-checked={soCampanha}>
            <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>Só em campanha
          </button>
          {dispSel.length === 0 && (
            <button type="button" role="switch" className={`cat-switch ${mostrarInativos ? 'ativo' : ''}`} onClick={() => setMostrarInativos((v) => !v)} aria-checked={mostrarInativos}>
              <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>Mostrar inativos
            </button>
          )}
          {filtrosAtivos > 0 && <button type="button" className="cat-limpar-tudo" onClick={limparFiltros}><i className="bi bi-x-circle" /> Limpar</button>}
        </div>
      )}
      <div className="cat-contador">{filtrados.length} produto{filtrados.length === 1 ? '' : 's'}{!mostrarInativos && !dispSel.length ? ' no mix ativo' : ''}</div>
    </>
  );

  return (
    <div>
      <h4 className="fw-bold mb-1"><i className="bi bi-grid me-2" style={{ color: 'var(--primary)' }} />Meu Mix</h4>
      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Os produtos que você fornece ao Centrão — catálogo e estoque por loja.</p>

      <div className="mb-3"><SeletorFornecedor value={fornSel} onChange={setFornSel} /></div>

      <div className="mm-tabs">
        <button className={`mm-tab ${aba === 'catalogo' ? 'ativa' : ''}`} onClick={() => setAba('catalogo')}><i className="bi bi-grid-3x3-gap" /> Catálogo</button>
        <button className={`mm-tab ${aba === 'estoque' ? 'ativa' : ''}`} onClick={() => setAba('estoque')}><i className="bi bi-shop" /> Estoque por loja</button>
        <button className={`mm-tab ${aba === 'amostras' ? 'ativa' : ''}`} onClick={() => setAba('amostras')}><i className="bi bi-box2-heart" /> Amostras</button>
      </div>

      {carregando ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} role="status" /></div>
      ) : erro ? (
        <div className="text-center text-danger py-4"><i className="bi bi-exclamation-triangle me-1" />{erro}</div>
      ) : (
        <>
          {aba !== 'amostras' && filtroBar}

          {aba === 'catalogo' && (
            filtrados.length === 0 ? (
              <div className="cat-vazio"><i className="bi bi-search" />Nenhum produto com esses filtros.</div>
            ) : (
              <>
                <div className="cat-grid">
                  {pagina1.map((p) => (
                    <CatalogoCard key={p.codpro} produto={p} apiUrl={apiUrl} onAbrir={() => irProduto(p.codpro)} />
                  ))}
                </div>
                {totalPaginas > 1 && (
                  <div className="cat-paginacao">
                    <button className="cat-pag-btn" disabled={paginaAtual <= 1} onClick={() => { setPagina(paginaAtual - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}><i className="bi bi-chevron-left" /></button>
                    <span className="cat-pag-sep">{paginaAtual} / {totalPaginas}</span>
                    <button className="cat-pag-btn" disabled={paginaAtual >= totalPaginas} onClick={() => { setPagina(paginaAtual + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}><i className="bi bi-chevron-right" /></button>
                  </div>
                )}
              </>
            )
          )}

          {aba === 'estoque' && (
            filtrados.length === 0 ? (
              <div className="cat-vazio"><i className="bi bi-search" />Nenhum produto com esses filtros.</div>
            ) : (
              <div className="mm-estoque-wrap">
                <table className="mm-estoque">
                  <thead>
                    <tr>
                      <th className="esq" onClick={() => sEst.toggle('nome')}>Produto {sEst.icon('nome')}</th>
                      {filiais.map((f) => <th key={f.codigo} onClick={() => sEst.toggle(f.codigo)}>{f.nome} {sEst.icon(f.codigo)}</th>)}
                      <th className="tot" onClick={() => sEst.toggle('total')}>Total {sEst.icon('total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sEst.aplicar(filtrados, (r, k) => (k === 'nome' ? r.nome : k === 'total' ? r.estoqueGrupo : (r.estoque[k] || 0))).map((p) => (
                      <tr key={p.codpro} className="linha-clicavel" onClick={() => irProduto(p.codpro)}>
                        <td className="esq prod">{p.nome}<br /><small>#{p.codpro} · {p.unid}</small></td>
                        {filiais.map((f) => {
                          const q = p.estoque[f.codigo];
                          return <td key={f.codigo} className={q > 0 ? '' : 'zero'}>{q > 0 ? Number(q).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '—'}</td>;
                        })}
                        <td className="tot">{Number(p.estoqueGrupo).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {aba === 'amostras' && (
            amCarreg ? (
              <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} role="status" /></div>
            ) : amErro ? (
              <div className="text-center text-danger py-4"><i className="bi bi-exclamation-triangle me-1" />{amErro}</div>
            ) : (
              <>
                <div className="cat-barra" style={{ marginBottom: 10 }}>
                  <div className="cat-busca-wrap">
                    <i className="bi bi-search" />
                    <input className="cat-busca" type="search" placeholder="Buscar produto..." value={busca} onChange={(e) => setBusca(e.target.value)} autoComplete="off" />
                    {busca && <button className="cat-busca-x" onClick={() => setBusca('')} type="button"><i className="bi bi-x-lg" /></button>}
                  </div>
                </div>
                <div className="cat-contador">{amostrasFiltradas.length} produto{amostrasFiltradas.length === 1 ? '' : 's'} com amostra em loja</div>
                {amostrasFiltradas.length === 0 ? (
                  <div className="cat-vazio"><i className="bi bi-box2-heart" />Nenhum produto seu com amostra em loja.</div>
                ) : (
                  <div className="mm-estoque-wrap">
                    <table className="mm-estoque">
                      <thead>
                        <tr>
                          <th className="esq" onClick={() => sAm.toggle('nome')}>Produto {sAm.icon('nome')}</th>
                          {amostras.lojas.map((l) => <th key={l.filial} className="amc" onClick={() => sAm.toggle(l.filial)}>{l.nome} {sAm.icon(l.filial)}</th>)}
                          <th className="tot" onClick={() => sAm.toggle('nLojas')}>Lojas {sAm.icon('nLojas')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sAm.aplicar(amostrasFiltradas, (r, k) => (k === 'nome' ? r.nome : k === 'nLojas' ? r.nLojas : (r.amostras[k] ? 1 : 0))).map((p) => (
                          <tr key={p.codpro} className="linha-clicavel" onClick={() => irProduto(p.codpro)}>
                            <td className="esq prod">{p.nome}<br /><small>#{p.codpro}</small></td>
                            {amostras.lojas.map((l) => {
                              const tem = p.amostras[l.filial] > 0;
                              return <td key={l.filial} className={`amc ${tem ? 'amc-sim' : 'amc-nao'}`}>{tem ? '✓' : '✗'}</td>;
                            })}
                            <td className="tot">{p.nLojas}/{amostras.lojas.length}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          )}
        </>
      )}
    </div>
  );
}
